// Состояние панели фильтров и правила отбора (п.20, 2026-09-07).
// Раньше жило в оболочке (FILTER/KSEL/WSEL/KONTURSEL + matchesWork и соседи).
// Здесь — маленький общий стор: панель его меняет, экраны и оболочка читают.
export type FilterState = {
  /** набранный текст в поле работ (действует, пока не выбраны работы) */
  name: string;
  /** выбранные работы (точное совпадение имён) */
  works: string[];
  /** выбранные корпуса; пусто = все */
  korpusa: string[];
  /** выбранные контуры; пусто = все */
  kontury: string[];
  /** etazh — один или несколько этажей через «|» (мультивыбор, 2026-09-15, #91);
      строка, а не список — старые наборы фильтров с одним этажом остаются годными */
  mesto: string; etazh: string; etap: string; podr: string; sistema: string;
};

/** выбранные этажи фильтра списком */
export const etazhiFiltra = (s: string): string[] => (s ? s.split('|').filter(Boolean) : []);
export type WorkMeta = { mesto?: string | null; etap?: string | null; sistema?: string | null; kontur?: string | null };

export const EMPTY: FilterState = {
  name: '', works: [], korpusa: [], kontury: [], mesto: '', etazh: '', etap: '', podr: '', sistema: '',
};

type Listener = () => void;

let state: FilterState = { ...EMPTY };
let meta: Record<string, WorkMeta> = {};
let pid = 0;
const listeners = new Set<Listener>();

function emit() { listeners.forEach(f => f()); }

/** Панель фильтров уже сообщила список корпусов проекта?
 *  До этого пустой выбор — это «ещё не знаем», а не «все корпуса»: если спросить
 *  у сервера все корпуса, шахматка на первом рисовании строит сетку по всему
 *  объекту (СБ3: 432 столбца, 24 700 px) и только потом сужается до выбранного.
 *  Поймано снимком экрана 2026-09-10. */
let gotov = false;

export const filterStore = {
  get(): FilterState { return state; },
  meta(): Record<string, WorkMeta> { return meta; },
  setMeta(m: Record<string, WorkMeta>) { meta = m; emit(); },
  set(patch: Partial<FilterState>) { state = { ...state, ...patch }; saveKorpusa(); emit(); },
  /** сброс снимает ВСЕ фильтры, включая корпуса (замечание владельца 2026-09-07):
      раньше выбранные корпуса переживали сброс, и тег «Корпус: К3» оставался */
  reset() { state = { ...EMPTY }; saveKorpusa(); emit(); },
  /** выбрать один корпус (переход по ссылке из «Проверок») */
  setKorpus(k: string) { state = { ...state, korpusa: [k] }; saveKorpusa(); emit(); },
  subscribe(f: Listener) { listeners.add(f); return () => { listeners.delete(f); }; },
  /** проект нужен для памяти выбранных корпусов */
  setProject(id: number) { pid = id; },
  /** восстановить выбор корпусов: сохранённый, иначе первый корпус.
      Пустой сохранённый список — это осознанный выбор «все корпуса» (после
      сброса фильтров), первый корпус подставляется только новичку (2026-09-07). */
  initKorpusa(korpusa: string[]) {
    if (state.korpusa.length || !korpusa.length) return;
    gotov = true;      // список корпусов пришёл: пустой выбор теперь значит «все»
    let saved: unknown = null;
    try { saved = JSON.parse(localStorage.getItem('plot_ksel_' + pid) || 'null'); } catch { /* приватный режим */ }
    if (Array.isArray(saved)) {
      const list = (saved as string[]).filter(k => korpusa.includes(k));
      // «все корпуса» — так и оставляем; но слушателям сообщаем: пустой выбор теперь
      // значит «все» (korpusValue → '*'), иначе экраны так и ждали бы список корпусов
      if (!list.length) { emit(); return; }
      state = { ...state, korpusa: list };
    } else {
      state = { ...state, korpusa: [korpusa[0]] };
    }
    emit();
  },
};

function saveKorpusa() {
  try { localStorage.setItem('plot_ksel_' + pid, JSON.stringify(state.korpusa)); } catch { /* приватный режим */ }
}

/** правила отбора по текущему состоянию — их оболочка отдаёт экранам */
export function matchers(f: FilterState = state, m: Record<string, WorkMeta> = meta) {
  const znaemKorpusa = gotov;
  const matchesWork = (name: string): boolean => {
    if (f.works.length) { if (!f.works.includes(String(name))) return false; }
    else if (f.name && !String(name).toLowerCase().includes(f.name.toLowerCase())) return false;
    const w = m[name];
    // работы нет в кэше свойств (только что создана/переименована) — не прячем
    if (!w) return true;
    if (f.mesto && w.mesto !== f.mesto) return false;
    if (f.etap && w.etap !== f.etap) return false;
    if (f.sistema && w.sistema !== f.sistema) return false;
    if (f.kontury.length) {
      // значение контура может содержать запятую («Ниже 0,000»), поэтому
      // проверяем и строку целиком, и её части (2026-09-09)
      const celoe = String(w.kontur || 'этаж').trim();
      const ks = [celoe, ...celoe.split(',').map(s => s.trim())];
      if (!ks.some(x => f.kontury.includes(x))) return false;
    }
    return true;
  };
  const matchesKorpus = (name: string) => !f.korpusa.length || f.korpusa.includes(name);
  const matchesEtazh = (e?: string | null) => !f.etazh || etazhiFiltra(f.etazh).includes(String(e ?? ''));
  const matchesPodr = (p?: string | null) => !f.podr || (p || '') === f.podr;
  return {
    matchesWork, matchesKorpus, matchesEtazh, matchesPodr,
    matchesTask: (t: { rabota: string; etazh?: string | null; podryadchik?: string | null }) =>
      matchesWork(t.rabota) && matchesEtazh(t.etazh) && matchesPodr(t.podryadchik),
    /** значение для параметра korpus в запросах: «*» — все */
    // пусто до готовности панели — сервер сам подставит первый корпус.
    // Готовность запоминаем на момент создания правил: набор правил живёт до
    // следующего изменения фильтра, и «живой» флаг успел бы превратить это же
    // пустое состояние в «все корпуса» (лишний запрос всего объекта, 2026-09-10)
    korpusValue: () => (f.korpusa.length ? f.korpusa.join(',') : (znaemKorpusa ? '*' : '')),
    /** первый выбранный корпус (шахматки, квартиры) */
    firstKorpus: (korpusa: string[]) => (f.korpusa[0] || korpusa[0] || ''),
    /** отпечаток фильтра — экраны по нему понимают, что пора пересчитать списки */
    filterKey: () => [f.name, f.works.join('|'), f.mesto, f.etap, f.sistema,
      f.kontury.join('|'), f.etazh, f.podr, f.korpusa.join('|')].join('¶'),
    /** фильтр по РАБОТАМ включён (Гант и схема связей приглушают «соседей»).
        Этаж и подрядчик сюда не входят: это фильтры задач, к схеме работ
        отношения не имеют (иначе схема прячет работы — ошибка 2026-09-07). */
    active: () => !!(f.name || f.works.length || f.mesto || f.etap || f.sistema
      || f.kontury.length),
    state: f,
  };
}
export type Matchers = ReturnType<typeof matchers>;
